import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MapMoveHandler from "./maps/MapMoveHandler";
import { get } from "lodash";
import { Card, CardContent } from "./ui/card";
import { Home, MapPin, Loader2, RefreshCw, Search } from "lucide-react";
import { Button } from "./ui/button";
import { formatCurrency } from "../utils/property-formatters";
import {
  applyListingPriceToProperty,
  getBuildingSquareFeet,
} from "../utils/property-calculations";
import API_CONSTRUCTOR from "../models/api-model";
import PropertyAPIs from "../models/property";

const apiModel = API_CONSTRUCTOR();
const propertyApi = new PropertyAPIs(apiModel);

// Module-level cache for fetched property details (avoids re-fetching on every click)
const detailsCache = new Map();
const DETAILS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/** @returns {number} distance in miles */
const haversineMiles = (lat1, lng1, lat2, lng2) => {
  const R = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toLatLngLiteral = (c) => {
  if (!c) return null;
  const lat = typeof c.lat === "function" ? c.lat() : c.lat;
  const lng = typeof c.lng === "function" ? c.lng() : c.lng;
  if (lat == null || lng == null) return null;
  return { lat: Number(lat), lng: Number(lng) };
};

const SEARCH_AREA_PAN_MILES = 0.35;
const SEARCH_AREA_IDLE_MS = 250;

const NearbyPropertiesMap = ({
  properties = [],
  centerProperty = null,
  height = "500px",
  onPropertyClick = null,
  customAction = null, // { text: string, onClick: (property) => void, icon?: Component }
  onSearchArea = null,
  searchAreaLoading = false,
}) => {
  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [freshPropertyData, setFreshPropertyData] = useState(null); // Latest data from property details API
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [centerPropertySelected, setCenterPropertySelected] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(17);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [marketFilter, setMarketFilter] = useState('onMarket'); // 'all', 'onMarket', 'offMarket'
  const [showSearchThisArea, setShowSearchThisArea] = useState(false);
  const searchBaselineRef = useRef(null);
  const lastCameraRef = useRef(null);
  const idleTimerRef = useRef(null);
  const searchAreaLoadingRef = useRef(false);
  const propertiesBaselineKey = useMemo(
    () =>
      `${properties?.length || 0}-${properties?.[0]?.id || ""}-${properties?.[properties.length - 1]?.id || ""}`,
    [properties]
  );

  useEffect(() => {
    searchBaselineRef.current = null;
    lastCameraRef.current = null;
    setShowSearchThisArea(false);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, [propertiesBaselineKey]);

  useEffect(() => {
    searchAreaLoadingRef.current = searchAreaLoading;
  }, [searchAreaLoading]);

  const handleCameraChanged = useCallback(
    (ev) => {
      if (!onSearchArea || !properties?.length) return;
      const center = toLatLngLiteral(ev?.detail?.center);
      if (!center) return;
      lastCameraRef.current = center;

      if (!searchBaselineRef.current) {
        searchBaselineRef.current = { ...center };
        setShowSearchThisArea(false);
        return;
      }

      const movedMi = haversineMiles(
        searchBaselineRef.current.lat,
        searchBaselineRef.current.lng,
        center.lat,
        center.lng
      );
      const movedEnough = movedMi >= SEARCH_AREA_PAN_MILES;

      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        if (searchAreaLoadingRef.current) return;
        setShowSearchThisArea(movedEnough);
      }, SEARCH_AREA_IDLE_MS);
    },
    [onSearchArea, properties?.length]
  );

  useEffect(() => {
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  // Fetch fresh property details when a property is selected
  const fetchPropertyDetails = useCallback(async (property) => {
    const pid = property.propertyId || property.id;
    if (!pid) return;

    // Check cache
    const cached = detailsCache.get(pid);
    if (cached && Date.now() - cached.timestamp < DETAILS_CACHE_DURATION) {
      applyListingPriceToProperty(cached.data);
      setFreshPropertyData(cached.data);
      setFetchingDetails(false);
      return;
    }

    setFetchingDetails(true);
    try {
      const result = await propertyApi.getPropertyDetails(pid);
      const freshProp = get(result, "property", null);
      if (freshProp) {
        applyListingPriceToProperty(freshProp);
        detailsCache.set(pid, { data: freshProp, timestamp: Date.now() });
        setFreshPropertyData(freshProp);
      }
    } catch (err) {
      console.warn("[NearbyPropertiesMap] Failed to fetch property details:", err);
    } finally {
      setFetchingDetails(false);
    }
  }, []);

  // Filter properties based on market status
  const filteredProperties = useMemo(() => {
    if (!properties || properties.length === 0) return [];
    
    if (marketFilter === 'onMarket') {
      return properties.filter(p => p.onMarket === true);
    } else if (marketFilter === 'offMarket') {
      return properties.filter(p => p.onMarket === false);
    }
    return properties; // 'all'
  }, [properties, marketFilter]);

  // Calculate property counts
  const propertyCounts = useMemo(() => {
    const total = properties.length;
    const onMarket = properties.filter(p => p.onMarket === true).length;
    const offMarket = properties.filter(p => p.onMarket === false).length;
    return { total, onMarket, offMarket };
  }, [properties]);

  // Calculate map bounds to fit all properties (including center property)
  const mapBounds = useMemo(() => {
    if (!filteredProperties || filteredProperties.length === 0) return null;

    const validProperties = filteredProperties.filter(
      (p) => p.latitude && p.longitude
    );

    if (validProperties.length === 0) return null;

    const lats = validProperties.map((p) => p.latitude);
    const lngs = validProperties.map((p) => p.longitude);

    // Include center property in bounds calculation
    if (centerProperty && centerProperty.latitude && centerProperty.longitude) {
      lats.push(centerProperty.latitude);
      lngs.push(centerProperty.longitude);
    }

    return {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs),
    };
  }, [filteredProperties, centerProperty]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  // Set initial center
  useEffect(() => {
    if (centerProperty && centerProperty.latitude && centerProperty.longitude) {
      setMapCenter({
        lat: centerProperty.latitude,
        lng: centerProperty.longitude,
      });
    } else if (properties && properties.length > 0) {
      // Use all properties to find center, not just filtered ones
      const firstProperty = properties.find((p) => p.latitude && p.longitude);
      if (firstProperty) {
        setMapCenter({
          lat: firstProperty.latitude,
          lng: firstProperty.longitude,
        });
      }
    }
  }, [centerProperty, properties]);

  // Auto-adjust zoom based on bounds
  useEffect(() => {
    if (mapBounds && filteredProperties.length > 1) {
      const latDiff = mapBounds.north - mapBounds.south;
      const lngDiff = mapBounds.east - mapBounds.west;
      const maxDiff = Math.max(latDiff, lngDiff);

      // Calculate appropriate zoom level (zoomed in closer)
      let zoom = 17;
      if (maxDiff > 0.5) zoom = 13;
      else if (maxDiff > 0.2) zoom = 14;
      else if (maxDiff > 0.1) zoom = 15;
      else if (maxDiff > 0.05) zoom = 16;
      else zoom = 17;

      setMapZoom(zoom);
    }
  }, [mapBounds, filteredProperties]);

  const handleMarkerClick = (property) => {
    // Clear any hover timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    // Clear hover state when clicking
    setHoveredProperty(null);
    setCenterPropertySelected(false); // Close center property popup
    setSelectedProperty(property);

    // Reset fresh data & fetch latest from property details API
    setFreshPropertyData(null);
    fetchPropertyDetails(property);
    
    // Only call onPropertyClick if there's no customAction
    // (customAction takes precedence - shown in popup)
    if (!customAction && onPropertyClick) {
      onPropertyClick(property);
    }
  };

  const handleCenterMarkerClick = () => {
    // Clear any hover timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setHoveredProperty(null);
    setSelectedProperty(null); // Close any nearby property popup
    setCenterPropertySelected(true);
  };

  const handleMarkerMouseEnter = (property) => {
    // Don't show hover if property is already selected
    if (selectedProperty?.id === property.id) return;
    
    // Debounce hover to prevent flickering
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    
    const timeout = setTimeout(() => {
      setHoveredProperty(property);
    }, 150); // 150ms delay to prevent flickering
    
    setHoverTimeout(timeout);
  };

  const handleMarkerMouseLeave = () => {
    // Clear the timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    
    // Small delay before clearing hover to prevent flicker when moving between marker and InfoWindow
    setTimeout(() => {
      setHoveredProperty(null);
    }, 100);
  };

  // Handle no properties at all case
  if (!properties || properties.length === 0) {
    return (
      <Card className="shadow-md">
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">
            No properties with valid coordinates
          </p>
        </CardContent>
      </Card>
    );
  }

  // If we don't have a map center yet, don't render the map
  if (!mapCenter) {
    return (
      <Card className="shadow-md">
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">
            Loading map...
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-md overflow-hidden relative h-full">
      {/* Empty Filter Message */}
      {filteredProperties.length === 0 && marketFilter !== 'all' && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 bg-white rounded-lg shadow-xl border-2 border-gray-300 p-6 text-center max-w-sm">
          <p className="text-gray-900 font-semibold mb-2">
            {marketFilter === 'onMarket' ? 'No On-Market Properties' : 'No Off-Market Properties'}
          </p>
          <p className="text-sm text-gray-600 mb-4">
            {marketFilter === 'onMarket' 
              ? 'There are no on-market properties in this area.'
              : 'There are no off-market properties in this area.'}
          </p>
          <button
            onClick={() => setMarketFilter('all')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
          >
            Show All Properties
          </button>
        </div>
      )}
      
      {/* Map Legend & Filter */}
      <div className="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-lg border border-gray-200">
        <div className="p-3 border-b border-gray-200">
          <p className="text-xs font-semibold mb-2.5 text-gray-900">Filter Properties</p>
          <div className="flex flex-col gap-1.5">
            <button
              onClick={() => setMarketFilter('all')}
              className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                marketFilter === 'all'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>All Properties</span>
              <span className="text-[10px] font-semibold">{propertyCounts.total}</span>
            </button>
            <button
              onClick={() => setMarketFilter('onMarket')}
              className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                marketFilter === 'onMarket'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                On Market
              </span>
              <span className="text-[10px] font-semibold">{propertyCounts.onMarket}</span>
            </button>
            <button
              onClick={() => setMarketFilter('offMarket')}
              className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                marketFilter === 'offMarket'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-600"></div>
                Off Market
              </span>
              <span className="text-[10px] font-semibold">{propertyCounts.offMarket}</span>
            </button>
          </div>
        </div>
        
        {/* Legend */}
        <div className="p-3">
          <p className="text-xs font-semibold mb-2 text-gray-900">Legend</p>
          <div className="space-y-1.5">
            {centerProperty && centerProperty.latitude && centerProperty.longitude && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500 ring-2 ring-orange-300"></div>
                <span className="text-xs text-gray-700 font-medium">Current Property</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-600"></div>
              <span className="text-xs text-gray-700">On Market</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-600"></div>
              <span className="text-xs text-gray-700">Off Market</span>
            </div>
          </div>
        </div>
      </div>
      
      <div style={{ height, width: "100%", position: "relative" }}>
        {onSearchArea && (showSearchThisArea || searchAreaLoading) && (
          <div className="absolute top-4 left-1/2 z-30 -translate-x-1/2 flex justify-center pointer-events-auto">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="shadow-lg gap-2 border border-border bg-background/95 backdrop-blur-sm hover:bg-muted"
              disabled={searchAreaLoading}
              onClick={() => {
                const c = lastCameraRef.current;
                if (!c || searchAreaLoading) return;
                onSearchArea(c);
              }}
            >
              {searchAreaLoading ? (
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              ) : (
                <Search className="h-4 w-4 shrink-0" />
              )}
              Search this area
            </Button>
          </div>
        )}
        <MapContainer
          center={[mapCenter.lat, mapCenter.lng]}
          zoom={mapZoom}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution="© OpenStreetMap © CARTO"
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            maxZoom={19}
          />
          <MapMoveHandler onCameraChanged={handleCameraChanged} />
            {filteredProperties.map((property) => {
              if (!property.latitude || !property.longitude) return null;

              const isSelected = selectedProperty?.id === property.id;
              const position = { lat: property.latitude, lng: property.longitude };

              // Format price for marker (e.g., 1.93M, 650K)
              const formatPriceShort = (price) => {
                if (!price) return 'N/A';
                if (price >= 1000000) {
                  return `${(price / 1000000).toFixed(2)}M`;
                } else if (price >= 1000) {
                  return `${Math.round(price / 1000)}K`;
                }
                return `${Math.round(price)}`;
              };

              const priceLabel = formatPriceShort(property.estimatedValue);
              const bgColor = property.onMarket ? "#2563eb" : "#dc2626";
              const bgColorLight = property.onMarket ? "#3b82f6" : "#ef4444";

              const markerIcon = L.divIcon({
                html: `<div style="background:${isSelected ? bgColorLight : bgColor};color:white;font-size:11px;font-weight:700;padding:4px 10px;border-radius:14px;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.25);white-space:nowrap;">${priceLabel}</div>`,
                className: "",
                iconAnchor: [40, 20],
              });

              return (
                <Marker
                  key={property.id}
                  position={[position.lat, position.lng]}
                  icon={markerIcon}
                  eventHandlers={{
                    click: () => handleMarkerClick(property),
                    mouseover: () => handleMarkerMouseEnter(property),
                    mouseout: handleMarkerMouseLeave,
                  }}
                />
              );
            })}

            {/* Current Property Pin */}
            {centerProperty && centerProperty.latitude && centerProperty.longitude && (() => {
              const centerPosition = { lat: centerProperty.latitude, lng: centerProperty.longitude };

              const centerMarkerIcon = L.divIcon({
                html: `<div style="background:#ea580c;color:white;font-size:11px;font-weight:700;padding:4px 12px;border-radius:14px;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);">📍 YOU</div>`,
                className: "",
                iconAnchor: [30, 20],
              });

              return (
                <Marker
                  key="center-property"
                  position={[centerPosition.lat, centerPosition.lng]}
                  icon={centerMarkerIcon}
                  eventHandlers={{ click: handleCenterMarkerClick }}
                />
              );
            })()}
        </MapContainer>
        {(selectedProperty || centerPropertySelected) && (
          <div className="absolute bottom-3 left-3 right-3 z-[1000] max-h-[55%] overflow-y-auto pointer-events-auto">
            <button
              type="button"
              className="absolute top-2 right-2 z-10 bg-white rounded-full w-8 h-8 shadow border text-lg leading-none"
              onClick={() => {
                setSelectedProperty(null);
                setFreshPropertyData(null);
                setCenterPropertySelected(false);
              }}
            >
              ×
            </button>
            {selectedProperty && (
              <PropertyInfoContent
                property={selectedProperty}
                freshData={freshPropertyData}
                isFetchingDetails={fetchingDetails}
                customAction={customAction}
              />
            )}
            {centerPropertySelected && centerProperty && (
              <CenterPropertyInfoContent property={centerProperty} />
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

// Property info content component (used in InfoWindow)
const PropertyInfoContent = ({ property, freshData = null, isFetchingDetails = false, customAction = null }) => {
  // Merge: prefer freshData values when available, fall back to original property
  const merged = freshData ? { ...property, ...freshData } : property;
  const displayValue = merged?.estimatedValue ?? property.estimatedValue;
  const displaySqft = getBuildingSquareFeet(merged);
  const displayYearBuilt = freshData?.yearBuilt || property.yearBuilt;
  const displayLotSize = freshData?.lotSquareFeet || freshData?.lotSize || property.lotSize;
  const displayBedrooms = freshData?.bedrooms ?? property.bedrooms;
  const displayBathrooms = freshData?.bathrooms ?? property.bathrooms;

  const pricePerSqft =
    displayValue && displaySqft
      ? Math.ceil(displayValue / displaySqft)
      : null;

  // Use fresh photo if available
  const propertyImage = freshData?.photoUrls?.[0] || freshData?.photoUrl || property.photoUrl || property.photoUrls?.[0] || null;
  const hasPhotos = freshData?.photosAvailable || property.photosAvailable || propertyImage;

  // Did the value change after fresh fetch?
  const valueChanged = freshData && freshData.estimatedValue && freshData.estimatedValue !== property.estimatedValue;

  return (
    <div className="w-full max-w-[90vw] sm:w-[420px] p-0 bg-white rounded-lg shadow-2xl overflow-hidden">
      {/* Fetching Latest Info Banner */}
      {isFetchingDetails && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-1.5 flex items-center justify-center gap-2">
          <Loader2 className="h-3 w-3 text-blue-600 animate-spin" />
          <span className="text-[11px] font-medium text-blue-700">Fetching latest info…</span>
        </div>
      )}

      {/* Updated Values Banner */}
      {!isFetchingDetails && freshData && (
        <div className={`${valueChanged ? 'bg-green-50 border-b border-green-200' : 'bg-gray-50 border-b border-gray-200'} px-4 py-1.5 flex items-center justify-center gap-2`}>
          <RefreshCw className={`h-3 w-3 ${valueChanged ? 'text-green-600' : 'text-gray-500'}`} />
          <span className={`text-[11px] font-medium ${valueChanged ? 'text-green-700' : 'text-gray-600'}`}>
            {valueChanged ? 'Updated with latest values' : 'Values are up to date'}
          </span>
        </div>
      )}

      {/* Top Section: Image + Price */}
      <div className="grid grid-cols-5 gap-2 sm:gap-3 px-4 sm:px-5 pt-3 sm:pt-4 pb-2 sm:pb-3">
        {/* Image */}
        <div className="col-span-2">
          {hasPhotos && propertyImage ? (
            <img
              src={propertyImage}
              alt={property.streetAddress}
              className="w-full h-20 sm:h-24 object-cover rounded border border-gray-200"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-full h-20 sm:h-24 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
              <Home className="h-6 w-6 text-gray-400" />
            </div>
          )}
        </div>

        {/* Price & Address */}
        <div className="col-span-3 flex flex-col justify-between">
          <div>
            <p className={`text-lg sm:text-2xl font-bold leading-tight ${valueChanged ? 'text-green-700' : 'text-gray-900'}`}>
              {formatCurrency(displayValue || 0)}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-500">
              Estimated Value
              {valueChanged && (
                <span className="ml-1 text-green-600 font-medium">✓ updated</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs sm:text-sm font-semibold text-blue-600 leading-tight line-clamp-1">
              {property.streetAddress || property.address}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-600">
              {property.city}, {property.state} {property.zip}
            </p>
          </div>
        </div>
      </div>

      {/* Property Stats - Compact Grid */}
      <div className="px-4 sm:px-5 py-2 sm:py-3 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center">
          <div>
            <p className="text-sm sm:text-base font-semibold text-gray-900">{displaySqft?.toLocaleString() || 'N/A'}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500">SqFt.</p>
          </div>
          <div>
            <p className="text-sm sm:text-base font-semibold text-green-600">{pricePerSqft ? `$${pricePerSqft.toLocaleString()}` : 'N/A'}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500">$/SqFt</p>
          </div>
          <div>
            <p className="text-sm sm:text-base font-semibold text-gray-900">{displayYearBuilt || 'N/A'}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500">Year Built</p>
          </div>
        </div>

        {/* Beds / Baths row (shown when fresh data provides them) */}
        {(displayBedrooms || displayBathrooms) && (
          <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-2 gap-2 sm:gap-3 text-center">
            <div>
              <p className="text-sm sm:text-base font-semibold text-gray-900">{displayBedrooms || 'N/A'}</p>
              <p className="text-[9px] sm:text-[10px] text-gray-500">Beds</p>
            </div>
            <div>
              <p className="text-sm sm:text-base font-semibold text-gray-900">{displayBathrooms || 'N/A'}</p>
              <p className="text-[9px] sm:text-[10px] text-gray-500">Baths</p>
            </div>
          </div>
        )}

        {displayLotSize && (
          <div className="mt-2 pt-2 border-t border-gray-100 text-center">
            <p className="text-sm sm:text-base font-semibold text-gray-900">{displayLotSize.toLocaleString()}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500">Lot SqFt.</p>
          </div>
        )}
      </div>


      {/* Action Buttons */}
      <div className="px-4 sm:px-5 pb-4 pt-2 space-y-2">
        {/* Custom Action Button (if provided) */}
        {customAction && (
          <button
            onClick={() => customAction.onClick(freshData ? { ...property, ...freshData } : property)}
            className="w-full bg-green-600 hover:bg-green-700 text-white text-center py-2 px-4 rounded text-xs font-medium transition-colors flex items-center justify-center gap-2"
          >
            {customAction.icon && <customAction.icon className="h-4 w-4" />}
            {customAction.text || 'Add Property'}
          </button>
        )}
        
        {/* Default View Details Button */}
        <button
          onClick={() => {
            const pid = property.propertyId || property.id;
            if (pid) {
              window.open(`/property/${pid}`, '_blank', 'noopener,noreferrer');
            }
          }}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-2 px-4 rounded text-xs font-medium transition-colors cursor-pointer"
        >
          View Property Details →
        </button>
      </div>
    </div>
  );
};

// Center property info content (shown when clicking the current property pin)
const CenterPropertyInfoContent = ({ property }) => {
  const propertyImage = property.photoUrl || property.photoUrls?.[0] || null;
  const hasPhotos = property.photosAvailable || propertyImage;

  return (
    <div className="w-full max-w-[90vw] sm:w-[380px] p-0 bg-white rounded-lg shadow-2xl overflow-hidden">
      {/* Header badge */}
      <div className="bg-orange-500 text-white text-center py-2 px-4">
        <p className="text-xs font-bold tracking-wide uppercase flex items-center justify-center gap-1.5">
          <MapPin className="h-3.5 w-3.5" />
          Current Property
        </p>
      </div>

      {/* Image + Address */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-0 sm:gap-3 px-4 sm:px-5 pt-4 pb-3">
        {/* Image */}
        <div className="col-span-1 sm:col-span-2 mb-3 sm:mb-0">
          {hasPhotos && propertyImage ? (
            <img
              src={propertyImage}
              alt={property.streetAddress}
              className="w-full h-28 sm:h-24 object-cover rounded border border-gray-200"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-28 sm:h-24 bg-orange-50 rounded border border-orange-200 flex items-center justify-center">
              <Home className="h-8 w-8 text-orange-400" />
            </div>
          )}
        </div>

        {/* Price & Address */}
        <div className="col-span-1 sm:col-span-3 flex flex-col justify-between">
          <div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {formatCurrency(property.estimatedValue || 0)}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Estimated Value</p>
          </div>
          <div className="mt-2 sm:mt-0">
            <p className="text-xs sm:text-sm font-semibold text-orange-600 leading-tight line-clamp-1">
              {property.streetAddress || property.address}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5">
              {property.city}, {property.state} {property.zip}
            </p>
          </div>
        </div>
      </div>

      {/* Property Stats */}
      <div className="px-4 sm:px-5 py-3 border-t border-gray-200">
        <div className="grid grid-cols-4 gap-2 sm:gap-3 text-center">
          <div>
            <p className="text-sm sm:text-base font-semibold text-gray-900">{property.bedrooms || 0}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500">Beds</p>
          </div>
          <div>
            <p className="text-sm sm:text-base font-semibold text-gray-900">{property.bathrooms || 0}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500">Baths</p>
          </div>
          <div>
            <p className="text-sm sm:text-base font-semibold text-gray-900">
              {getBuildingSquareFeet(property)?.toLocaleString() || "N/A"}
            </p>
            <p className="text-[9px] sm:text-[10px] text-gray-500">SqFt.</p>
          </div>
          <div>
            <p className="text-sm sm:text-base font-semibold text-gray-900">{property.yearBuilt || 'N/A'}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500">Year Built</p>
          </div>
        </div>
      </div>

      {/* View Details */}
      <div className="px-4 sm:px-5 pb-4 pt-2">
        <button
          onClick={() => {
            const pid = property.propertyId || property.id;
            if (pid) {
              window.open(`/property/${pid}`, '_blank', 'noopener,noreferrer');
            }
          }}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white text-center py-2 px-4 rounded text-xs font-medium transition-colors cursor-pointer"
        >
          View Property Details →
        </button>
      </div>
    </div>
  );
};

export default NearbyPropertiesMap;
